import React from "react";
import { ContentSpacer } from "../common/layout";
import { Button } from "../../components/ui/button";
import { DialogTrigger } from "../../components/ui/dialog";
import { useOrg } from "../../auth/org-provider";
import { Mail, Slack, UserPlus, EditIcon } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "../../components/ui/table";
import { useEffect } from "react";
import Spinner from "../../components/ui/spinner";
import { useSearchParams } from "react-router-dom";
import { config } from "../../lib/config";
import { useInviteUserMutation } from "../../data/mutations/invite-user";
import { Org } from "../../data/service/tekkr-service-types";
import ConfirmationDialog from "../../components/modals/confirmation-dialog";
import { AvatarForUser } from "../../components/shared/org/avatar-for-user";
import PersonDialog from "../../components/modals/person-dialog";
import { useImportSlackUsersMutation } from "../../data/mutations/import-slack-users";
import { useOAuthServiceAction } from "../../data/hooks/connect-oauth-account-button";
import { OAuthService } from "tekkr-common/dist/oauth";
import { EditableNumberField, EditableStageField } from "../../components/shared/org/editable-org-meta-field";

function UserRow({ u }: { u: Org["users"][0] }) {
    const inviteMutation = useInviteUserMutation();
    async function inviteUser() {
        await inviteMutation.mutateAsync({
            userId: u.id,
            sendEmail: true,
        });
    }

    return (
        <>
            <TableRow>
                <TableCell>
                    <div className={"flex flex-row items-center gap-2"}>
                        <AvatarForUser className={"h-8 w-8"} user={u} />
                        {u.name}
                    </div>
                </TableCell>
                <TableCell className={"text-muted-foreground"}>
                    {u.title ? u.title : <span className={"opacity-45"}>&mdash;</span>}
                </TableCell>
                <TableCell className={"text-muted-foreground"}>
                    {u.department ? u.department : <span className={"opacity-45"}>&mdash;</span>}
                </TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell className={"flex flex-row items-center justify-end gap-2"}>
                    {!u.hasAccount && (
                        <>
                            <ConfirmationDialog
                                title={"Send email invite?"}
                                body={<p>Do you want to send an email invite to {u.name}?</p>}
                                confirm={inviteUser}
                            >
                                <DialogTrigger asChild>
                                    <Button
                                        variant={"outline-primary"}
                                        className={inviteMutation.isPending ? "invisible" : undefined}
                                        size={"sm"}
                                    >
                                        <Mail className={"me-2 h-5 w-5"} />
                                        Invite
                                    </Button>
                                </DialogTrigger>
                            </ConfirmationDialog>
                            {inviteMutation.isPending && <Spinner />}
                        </>
                    )}
                    <PersonDialog context="org" editMode={{ user: u }}>
                        <DialogTrigger asChild>
                            <Button
                                size={"sm"}
                                variant={"ghost"}
                                className={"text-muted-foreground transition-colors duration-200 hover:text-foreground"}
                            >
                                <EditIcon className={"h-5 w-5"} />
                                <span className={"ms-2 text-sm font-medium"}>Edit</span>
                            </Button>
                        </DialogTrigger>
                    </PersonDialog>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function OrgPage() {
    const org = useOrg();
    const [queryParams, setQueryParams] = useSearchParams();

    const importSlackUsersMutation = useImportSlackUsersMutation({
        onSuccess: () => {
            queryParams.delete("import_users");
            setQueryParams(queryParams);
        },
    });

    useEffect(() => {
        document.title = "Manage Organization - Tekkr";
    });

    const importSlackUsers = useOAuthServiceAction(OAuthService.Slack, importSlackUsersMutation.mutateAsync);

    return (
        <ContentSpacer>
            <h1 className={"slide-in-top-variant-1"}>Your Organization: {org.name}</h1>
            <div className={"flex flex-row items-center gap-4"}>
                <div className={"flex flex-row flex-wrap items-start gap-8"}>
                    <EditableNumberField
                        label="Tech Org Size"
                        value={org.meta?.techOrgSize}
                        field="techOrgSize"
                        validate={(v) =>
                            org.meta?.companySize !== undefined && v > org.meta.companySize
                                ? "Cannot exceed company size"
                                : null
                        }
                    />
                    <EditableNumberField
                        label="Company Size"
                        value={org.meta?.companySize}
                        field="companySize"
                        validate={(v) =>
                            org.meta?.techOrgSize !== undefined && v < org.meta.techOrgSize
                                ? "Cannot be smaller than tech org"
                                : null
                        }
                    />
                    <EditableStageField value={org.meta?.stage} />
                </div>
                {config.env !== "prod" && (
                    <div
                        className={
                            "construction-tape-bg flex flex-row flex-wrap items-start gap-8 rounded-lg px-3 py-1"
                        }
                    >
                        <div className={"flex flex-col"}>
                            <span className={"text-xs font-semibold text-muted-foreground"}>Office Suite</span>
                            <span className={"font-medium"}>{org.officeSuiteProvider}</span>
                        </div>
                    </div>
                )}
            </div>
            <hr className={"zoom-in-line mb-4 mt-4"} />
            <p className={"slide-in-bottom-variant-2"}>
                Need to bring more team members on board? You can easily add new users to your organization right from
                this page. Expand your team's capabilities and keep everyone in the loop.
            </p>
            <div className={"slide-in-bottom-variant-1 flex flex-row items-center gap-4"}>
                <h3>Members</h3>
                <PersonDialog context={"org"}>
                    <DialogTrigger asChild>
                        <Button size={"sm"} className={"duration-300 animate-in fade-in-50 slide-in-from-left-1"}>
                            <UserPlus className={"h-5 w-5"} />
                            <span className={"ms-2"}>Add member</span>
                        </Button>
                    </DialogTrigger>
                </PersonDialog>
                <Button
                    onClick={() => importSlackUsers.action()}
                    size={"sm"}
                    variant={"secondary"}
                    className={"duration-500 animate-in fade-in-50 slide-in-from-left-4"}
                >
                    <Slack className={"me-2 h-5 w-5"} /> Import users from Slack
                </Button>
            </div>

            <ConfirmationDialog
                open={queryParams.get("import_users") === "slack"}
                title={"Import Slack Members?"}
                body={<p>Do you want to import all members of your slack workspace?</p>}
                confirm={async () => {
                    await importSlackUsersMutation.mutateAsync();
                }}
            />

            <Table className={"slide-in-bottom-variant-3"}>
                <TableBody>
                    <TableRow>
                        <TableHead className="w-[200px]">Name</TableHead>
                        <TableHead className="w-[200px]">Title</TableHead>
                        <TableHead className="w-[150px]">Department</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead></TableHead>
                    </TableRow>
                    {org.users.map((u) => (
                        <UserRow key={u.id} u={u} />
                    ))}
                </TableBody>
            </Table>
        </ContentSpacer>
    );
}
